import React from 'react'
import { getGrooverText, getGrooverTextStyle } from '../libs/GrooverTextRepository'
import compactTexts from '../../../pure-js/assets/compact-texts.json'
import { Language, TextKeyWithOnlyText } from '../../../pure-js/types/Antiloop'
import { useCustomerId } from '../context/CustomerContext'

interface GrooverTextProps {
  textKey: string | TextKeyWithOnlyText
  language?: Language
  className?: string
  style?: React.CSSProperties
  children?: React.ReactNode
  variant?: string // For design system style variants
  customerId?: string // Optional override for customer ID
}

const DEFAULT_TEXT_COLOR = '#000000'
const BUTTON_STYLE_PREFIX = 'button.'
const BUTTONS_STYLE_PREFIX = 'buttons.'

const GrooverText: React.FC<GrooverTextProps> = ({
  textKey,
  language,
  className,
  style,
  children,
  variant,
  customerId
}) => {
  const contextCustomerId = useCustomerId()
  const effectiveCustomerId = customerId ?? contextCustomerId ?? undefined

  // Handle both string keys and legacy TextKeyWithOnlyText objects
  let text: string
  if (typeof textKey === 'string') {
    text = getGrooverText(textKey, language, effectiveCustomerId)
  } else if (textKey && typeof textKey === 'object') {
    // Legacy format
    text = textKey.texts?.[language || 'sv'] || textKey.characters || ''
  } else {
    text = ''
  }

  // Get the style key from compact-texts.json for this textKey
  const rawStyleKey =
    variant ||
    (typeof textKey === 'string' ? compactTexts[textKey]?.s : undefined) ||
    (typeof textKey === 'object' ? undefined : textKey)

  // Normalize legacy/plural style keys like "buttons.primary" -> "button.primary"
  // so we can correctly resolve typography from design-system.json.
  const styleKey =
    rawStyleKey && typeof rawStyleKey === 'string' && rawStyleKey.startsWith(BUTTONS_STYLE_PREFIX)
      ? `${BUTTON_STYLE_PREFIX}${rawStyleKey.slice(BUTTONS_STYLE_PREFIX.length)}`
      : rawStyleKey

  const designSystemStyle =
    styleKey && typeof styleKey === 'string'
      ? getGrooverTextStyle(styleKey)
      : typeof textKey === 'object' && textKey.style
        ? textKey.style
        : {}

  // Merge design system style with any custom style props
  const mergedStyle: React.CSSProperties = {
    ...designSystemStyle,
    ...style
  }

  // Force readable text in user-web (light background)
  // If a caller explicitly passes style.color, respect it.
  // Do NOT force for button typography keys since CTA buttons are dark and need white text.
  const isButtonText = styleKey && typeof styleKey === 'string' && styleKey.startsWith(BUTTON_STYLE_PREFIX)
  if (!style?.color && !isButtonText) {
    mergedStyle.color = DEFAULT_TEXT_COLOR
  }

  if (children) {
    return (
      <span className={className} style={mergedStyle}>
        {text}
        {children}
      </span>
    )
  }

  return (
    <span className={className} style={mergedStyle}>
      {text}
    </span>
  )
}

export default GrooverText
